import { useState } from "react";
import { FaShare, FaDownload } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Button from "./Button";

interface ShareVideoButtonProps {
  videoFile: File;
  title?: string;
  text?: string;
}

export default function ShareVideoButton({ videoFile }: ShareVideoButtonProps) {
  const { t } = useTranslation();
  const [shareError, setShareError] = useState<string | null>(null);

  const canShare = () => {
    return (
      typeof navigator !== "undefined" &&
      navigator.canShare &&
      navigator.canShare({ files: [videoFile] })
    );
  };

  const handleDownload = () => {
    const url = URL.createObjectURL(videoFile);
    const link = document.createElement("a");
    link.href = url;
    link.download = videoFile.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleShare = async () => {
    setShareError(null);

    try {
      if (canShare()) {
        await navigator.share({
          files: [videoFile],
        });
        console.log("Video shared successfully");
      } else {
        handleDownload();
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "AbortError") {
          console.log("Share cancelled");
        } else {
          console.error("Error sharing:", error);
          setShareError(t("share-button.error"));
        }
      }
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {canShare() ? (
        <Button variant="tertiary" onPress={handleShare}>
          <div className="flex items-center gap-2">
            <FaShare />
            <span>{t("share-button.share")}</span>
          </div>
        </Button>
      ) : (
        <Button variant="tertiary" onPress={handleDownload}>
          <div className="flex items-center gap-2">
            <FaDownload />
            <span>{t("share-button.download")}</span>
          </div>
        </Button>
      )}

      {shareError && <p className="text-center text-sm text-red-500">{shareError}</p>}
    </div>
  );
}
